'use client';

import React, { useState } from 'react';
import { AlertTriangle, Lock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

interface LotClosureConfirmProps {
  lotNumber: string;
  pendingAmount: number;
  onClose: () => void;
}

export default function LotClosureConfirm({ lotNumber, pendingAmount, onClose }: LotClosureConfirmProps) {
  const [isClosing, setIsClosing] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const handleClose = async () => {
    if (!confirmed) return;
    setIsClosing(true);
    // Backend integration point: POST /api/lots/:id/close — sets status to 'closed', locks all records
    await new Promise((r) => setTimeout(r, 1500));
    setIsClosing(false);
    toast.success(`Lot #${lotNumber} closed successfully — Settlement report generated`);
    onClose();
  };

  return (
    <div className="space-y-4">
      {pendingAmount > 0 && (
        <div className="flex items-start gap-3 bg-warning/10 border border-warning/20 rounded-xl p-3">
          <AlertTriangle size={16} className="text-warning flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-warning">Outstanding Balance Exists</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              ₹{pendingAmount.toLocaleString('en-IN')} is still pending from buyers. Closing the lot will lock all records. Ensure you have collected or written off this amount.
            </p>
          </div>
        </div>
      )}

      <div className="bg-muted/30 rounded-xl p-4 space-y-2">
        <p className="text-sm font-semibold text-foreground">Closing Lot #{lotNumber} will:</p>
        <ul className="space-y-1.5">
          {[
            'Lock all sales records — no further edits',
            'Lock all payment records',
            'Generate final settlement report',
            'Mark lot as permanently closed',
            'Cannot be reopened without Admin approval',
          ].map((item) => (
            <li key={`closure-item-${item.slice(0, 20)}`} className="flex items-start gap-2 text-xs text-muted-foreground">
              <span className="text-warning mt-0.5">•</span>
              {item}
            </li>
          ))}
        </ul>
      </div>

      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={confirmed}
          onChange={(e) => setConfirmed(e.target.checked)}
          className="mt-0.5 w-4 h-4 rounded border-border accent-primary"
        />
        <span className="text-xs text-foreground font-medium">
          I confirm that all sales, payments, and expenses are verified and this lot is ready for final closure.
        </span>
      </label>

      <div className="flex gap-3">
        <button onClick={onClose} className="btn-secondary flex-1">Cancel</button>
        <button
          onClick={handleClose}
          disabled={!confirmed || isClosing}
          className="btn-primary flex-1"
        >
          {isClosing ? (
            <><Loader2 size={14} className="animate-spin" />Closing...</>
          ) : (
            <><Lock size={14} />Close Lot #{lotNumber}</>
          )}
        </button>
      </div>
    </div>
  );
}